import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/BrandLogo";
import { Trash2, Mail, ShieldAlert, Clock } from "lucide-react";

// Feb-21: Play Store compliant account-deletion landing page. Google Play requires
// a public URL where users can start the deletion process without logging in.
export default function DeleteAccount() {
  return (
    <div className="min-h-screen bg-[#050914] text-slate-200">
      <header className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/"><BrandLockup size="sm"/></Link>
        <Link to="/" className="text-xs text-nb-cyan hover:text-white">← Back to Home</Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 pb-20">
        <div className="text-xs uppercase tracking-widest text-nb-cyan mb-2">Account</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tight">Delete your account</h1>
        <p className="mt-3 text-sm text-slate-400">You can request permanent deletion of your Next Billion account and all associated personal data at any time.</p>

        <div className="mt-6 nb-card p-5 border-red-400/30 flex items-start gap-3">
          <ShieldAlert size={22} className="text-red-400 shrink-0 mt-0.5"/>
          <div className="text-sm">
            <div className="text-white font-semibold">Before you delete</div>
            <p className="mt-1 text-slate-300 leading-relaxed">Deleting your account is <b>permanent</b>. Your team relationships, active bots, credit line and NB Token balance will all be closed. Please withdraw any pending balance to your BEP20 wallet <b>before</b> requesting deletion.</p>
          </div>
        </div>

        <Section icon={Trash2} title="How to request deletion">
          <ol className="list-decimal list-inside space-y-2">
            <li>Email us at <a className="text-nb-cyan font-mono" href="mailto:support@nextbillion.world?subject=Account%20Deletion%20Request">support@nextbillion.world</a> from the same email address registered with your Next Billion account.</li>
            <li>Use the subject line: <span className="font-mono text-white">Account Deletion Request</span>.</li>
            <li>In the body, include your <b>registered email</b> and your <b>referral code</b> (visible on your Dashboard) so we can locate your account.</li>
            <li>We will confirm within <b>72 hours</b> and complete the deletion within <b>30 days</b>, unless legal financial-record retention prevents it (see below).</li>
          </ol>
        </Section>

        <Section icon={Mail} title="What data is deleted">
          <ul className="list-disc list-inside space-y-1">
            <li>Your profile (name, email, password hash, referral code, wallet addresses).</li>
            <li>Your KYC documents (ID, selfie, address).</li>
            <li>Your notification history, chat/support messages, and device sessions.</li>
            <li>Your bot investment history that is fully settled.</li>
          </ul>
        </Section>

        <Section icon={Clock} title="What is retained (legal requirement)">
          <ul className="list-disc list-inside space-y-1">
            <li>Anonymised transaction ledger — the on-chain USDT deposits and withdrawals we processed — kept for up to <b>5 years</b> to comply with anti-money-laundering and tax rules.</li>
            <li>Anonymised aggregate analytics (no PII attached).</li>
          </ul>
          <p className="mt-3">After the retention period expires, these records are permanently anonymised or destroyed.</p>
        </Section>

        <Section icon={Mail} title="Prefer in-app deletion?">
          <p>If you can still log in, you can also start the deletion from inside the app: <b>Settings → Danger Zone → Delete Account</b>. Both routes trigger the same manual review by our compliance team.</p>
        </Section>

        <div className="mt-10 border-t border-white/10 pt-6 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-3">
          <div>© 2026 Next Billion. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/" className="hover:text-white">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-nb-cyan"/>
        <h2 className="font-heading font-bold text-xl text-white">{title}</h2>
      </div>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
